import { useEffect, useMemo, useState, useSyncExternalStore } from 'react';
import { Plus, Trash2, ChevronsLeft, UserPlus, ArrowLeft } from '../../icons';
import type { TaskProjectDto } from '../../api/tasks';
import { listTeamMembers, type TeamMemberDto, type TeamDto } from '../../api/project-teams';
import { scopeKey, parseScopeKey, type TaskScope } from './task-scope';
import { tTask, getTaskLang, subscribeTaskLang } from '../../lib/task-i18n';
import { isProjectCreator } from '../ProjectPage/confirmDeleteProject';
import { projectColor } from './ProjectTopBar';

interface ProjectSidePanelProps {
  projects: TaskProjectDto[];
  activeProjectId: string | null;
  onSelect: (id: string) => void;
  /** 팀 스코프 (design D-1). 미전달 시 선택기 미렌더 — 기존 호출부 무영향. */
  scope?: TaskScope;
  /** 내 팀 목록 — 스코프 선택기 옵션. */
  teams?: TeamDto[];
  onScopeChange?: (scope: TaskScope) => void;
  /** [＋ 새 프로젝트] — 상세 생성 모달(ProjectCreateModal) 오픈. */
  onNew: () => void;
  /** 카드 hover 휴지통 → 삭제 (미전달 시 버튼 미렌더). 생성자 본인만 노출. */
  onDelete?: (project: TaskProjectDto) => void;
  /** 패널 접기(«). 미전달 시 버튼 미렌더. */
  onCollapse?: () => void;
}

/**
 * ProjectSidePanel (2026-07-24) — /task 의 **2차 좌측 '내 프로젝트' 패널**.
 *
 * 레이아웃 SSOT: `docs/all-frame.html` 3d(프로젝트 · 상세) —
 * "좌측 2차 상세 = 내 프로젝트를 카드형으로 나열(진행/완료 · 진행률·기간·멤버)".
 * 종전에는 같은 목록이 상단 가로 바(`ProjectTopBar`, v2.7)에 있었는데, 시안의 위치(좌측 2차)로
 * 되돌린 것이다. **목록의 위치·형태만** 바뀌며 카드가 담는 정보·동작(선택/생성/삭제)은 그대로다.
 *
 * 표시 정보는 기존 API 그대로 쓴다(서버 무변경):
 *   - 컬러 스트립: `stripColor(id)` — ProjectTopBar 와 같은 해시 팔레트라 색이 일관된다.
 *   - 진행률: `doneTaskCount / taskCount` (완료 그룹 판정도 동일 기준).
 *   - 기간: 시작일 필드가 없으므로 `dueDate` 기준 마감일 · D-day 만 표시한다.
 *   - 멤버: 소속 팀 멤버 미리보기(팀당 1회 조회, 상한 10팀) — ProjectTopBar 와 동일 방식.
 */
export function ProjectSidePanel({
  projects, activeProjectId, onSelect, onNew, onDelete, onCollapse,
  scope, teams, onScopeChange,
}: ProjectSidePanelProps) {
  useSyncExternalStore(subscribeTaskLang, getTaskLang, getTaskLang);
  const [teamPreview, setTeamPreview] = useState<Record<string, TeamMemberDto[]>>({});

  // 멤버 미리보기 대상 팀 = 프로젝트의 teamId ∩ 내가 속한 팀.
  //
  // 교집합을 내는 이유: 프로젝트 목록은 **ProjectMember** 기준으로 보이는데
  // GET /project-teams/:id/members 는 **ProjectTeamMember** 기준이라, 팀 멤버가 아닌
  // 프로젝트 멤버(팀 멤버 변경이 기존 프로젝트에 소급되지 않는 snapshot 정책의 정상 결과)가
  // 조회하면 서버가 404 TEAM_NOT_FOUND 를 낸다(비-멤버에게 팀 존재를 숨기는 의도된 정책).
  // 어차피 받지 못할 팀을 요청해 콘솔을 404 로 채우던 것을 호출 전에 걸러낸다.
  // teams 미전달 호출부에서는 필터를 적용하지 않아 기존 동작이 그대로 유지된다.
  const myTeamIds = useMemo(
    () => (teams ? new Set(teams.map((t) => t.id)) : null),
    [teams],
  );

  useEffect(() => {
    // 2026-08-07 — 서버가 memberPreview(ProjectMember 기준)를 실어 보내면 그 프로젝트는
    // 팀 조회가 아예 필요 없다. 목록 전체가 preview 를 갖고 있으면 요청 0건이 된다.
    // (구버전 서버 응답 등 preview 가 없는 프로젝트만 기존 팀 기준 경로로 폴백)
    const teamIds = [...new Set(
      projects.filter((p) => !hasMemberPreview(p)).map((p) => p.teamId).filter((v): v is string => !!v),
    )]
      .filter((id) => myTeamIds === null || myTeamIds.has(id))
      .slice(0, 10);
    if (teamIds.length === 0) return;
    let cancelled = false;
    void (async () => {
      const results = await Promise.allSettled(teamIds.map((id) => listTeamMembers(id)));
      if (cancelled) return;
      const next: Record<string, TeamMemberDto[]> = {};
      results.forEach((r, i) => {
        if (r.status === 'fulfilled') next[teamIds[i]!] = r.value;
      });
      setTeamPreview((prev) => ({ ...prev, ...next }));
    })();
    return () => { cancelled = true; };
  }, [projects, myTeamIds]);

  // 진행/완료 그룹 — 태스크가 있고 전부 done 이거나 아카이브면 완료(서버 상태 필드 없음).
  const { active, done } = useMemo(() => {
    const a: TaskProjectDto[] = [];
    const d: TaskProjectDto[] = [];
    for (const p of projects) (isDone(p) ? d : a).push(p);
    return { active: a, done: d };
  }, [projects]);

  function card(p: TaskProjectDto) {
    const total = p.taskCount ?? 0;
    const doneCount = p.doneTaskCount ?? 0;
    const pct = total > 0 ? Math.round((doneCount / total) * 100) : 0;
    // 2026-08-07 — 아바타는 **프로젝트 멤버(ProjectMember)** 기준이 우선.
    // 목록 자체가 그 기준으로 보이므로 카드에 뜨는 얼굴도 같은 기준이어야 맞다.
    // 팀 기준(teamPreview)은 preview 가 없을 때만 쓰는 폴백으로 남긴다.
    const preview = p.memberPreview;
    const members: Array<{ userId: string; name: string; avatarUrl: string | null }> =
      preview ?? ((p.teamId && teamPreview[p.teamId]) || []);
    const shown = members.slice(0, 3);
    // preview 는 상한(5명)이 걸려 있어 길이로 총원을 셀 수 없다 — memberCount 를 쓴다.
    const memberTotal = preview ? (p.memberCount ?? members.length) : members.length;
    const extra = memberTotal - shown.length;
    const finished = isDone(p);

    return (
      <div key={p.id} className="task-side-card-wrap">
        <button
          type="button"
          role="tab"
          aria-selected={p.id === activeProjectId}
          aria-label={tTask('task.topbar.card_aria', { name: p.name })}
          className={`task-side-card${p.id === activeProjectId ? ' task-side-card--on' : ''}`}
          onClick={() => onSelect(p.id)}
        >
          <span className="task-side-card-strip" style={{ background: projectColor(p) }} aria-hidden="true" />

          <span className="task-side-card-head">
            <span className="task-side-card-name">{p.name}</span>
            <span className={`task-side-card-badge${finished ? ' task-side-card-badge--done' : ''}`}>
              {tTask(finished ? 'task.side.badge_done' : 'task.side.badge_active')}
            </span>
          </span>

          <span className="task-side-card-meta">
            <span>{p.teamName ?? tTask('task.topbar.personal')}</span>
            {p.dueDate && (
              <>
                <span aria-hidden="true">·</span>
                <span>{tTask('task.side.due', { date: shortDate(p.dueDate) })}</span>
                <span className="task-side-card-dday">{ddayLabel(p.dueDate)}</span>
              </>
            )}
          </span>

          <span
            className="task-side-card-bar"
            role="img"
            aria-label={tTask('task.side.progress_aria', { name: p.name, n: String(pct) })}
          >
            <span className="task-side-card-bar-fill" style={{ width: `${pct}%` }} />
          </span>

          <span className="task-side-card-foot">
            <span className="task-pcard-stack" aria-hidden="true">
              {shown.map((m) => (
                m.avatarUrl ? (
                  <img key={m.userId} className="task-pcard-avatar" src={m.avatarUrl} alt="" />
                ) : (
                  <span key={m.userId} className="task-pcard-avatar task-pcard-avatar--initial">
                    {m.name.slice(0, 1)}
                  </span>
                )
              ))}
              {extra > 0 && (
                <span className="task-pcard-avatar task-pcard-avatar--initial">
                  {tTask('task.topbar.more_members', { n: String(extra) })}
                </span>
              )}
            </span>
            <span className="task-side-card-count">{doneCount}/{total} · {pct}%</span>
          </span>
        </button>

        {/* 생성자 본인만 휴지통 노출 (서버 FORBIDDEN_NOT_CREATOR 의 UI 미러 — 기존 정책 유지) */}
        {onDelete && isProjectCreator(p) && (
          <button
            type="button"
            className="task-side-card-del"
            aria-label={tTask('task.topbar.delete_aria', { name: p.name })}
            onClick={(e) => { e.stopPropagation(); onDelete(p); }}
          >
            <Trash2 size={12} />
          </button>
        )}
      </div>
    );
  }

  return (
    <aside className="task-side" aria-label={tTask('task.side.aria')}>
      {/* 3c 프로젝트 홈으로 복귀 (design-20260725) — TaskPage 는 Router 없이 렌더되는 계약이라 정적 anchor. */}
      <a className="task-side-back" href="/project" title={tTask('task.side.back_home')}>
        <ArrowLeft size={13} aria-hidden />
        {tTask('task.side.back_home')}
      </a>

      <div className="task-side-head">
        <span className="task-side-title">{tTask('task.side.heading')}</span>
        {onCollapse && (
          <button
            type="button"
            className="task-side-collapse"
            onClick={onCollapse}
            aria-label={tTask('task.side.collapse')}
            title={tTask('task.side.collapse')}
          >
            <ChevronsLeft size={14} aria-hidden />
          </button>
        )}
      </div>

      {/* 팀 스코프 (design-20260724 D-1) — 파라미터 없이 들어와도 "어느 팀을 보고 있는지"가 보인다.
          미전달(scope/onScopeChange 없음)이면 렌더하지 않아 기존 호출부 동작이 그대로다. */}
      {scope && onScopeChange && (
        <div className="task-side-scope">
          <label className="task-side-scope-lb" htmlFor="task-scope-select">{tTask('task.scope.label')}</label>
          <select
            id="task-scope-select"
            className="task-side-scope-select"
            aria-label={tTask('task.scope.aria')}
            value={scopeKey(scope)}
            onChange={(e) => {
              const next = parseScopeKey(e.target.value);
              if (next) onScopeChange(next);
            }}
          >
            <option value="all">{tTask('task.scope.all')}</option>
            {(teams ?? []).map((t) => (
              <option key={t.id} value={`team:${t.id}`}>{t.name}</option>
            ))}
            <option value="personal">{tTask('task.scope.personal')}</option>
          </select>
          {/* 새 팀 — 좁은 스코프 줄이라 아이콘만(2026-07-24 요청). 라벨은 title/aria 로 유지. */}
          <a
            className="task-side-scope-new"
            href="/project/teams"
            title={tTask('task.scope.new_team')}
            aria-label={tTask('task.scope.new_team')}
          >
            <UserPlus size={14} aria-hidden />
          </a>
        </div>
      )}

      <div className="task-side-body scrollbar-thin" role="tablist" aria-label={tTask('task.topbar.aria')}>
        {active.length > 0 && (
          <>
            <div className="task-side-group">{tTask('task.side.group_active')}</div>
            {active.map(card)}
          </>
        )}
        {done.length > 0 && (
          <>
            <div className="task-side-group">{tTask('task.side.group_done')}</div>
            {done.map(card)}
          </>
        )}

        {scope && active.length === 0 && done.length === 0 && (
          <p className="task-side-empty" role="status">{tTask('task.scope.empty')}</p>
        )}

        <button type="button" className="task-side-new" onClick={onNew}>
          <Plus size={14} aria-hidden />
          {tTask('task.topbar.new')}
        </button>
      </div>
    </aside>
  );
}

/**
 * 서버가 이 프로젝트의 멤버 미리보기를 실어 보냈는가 (2026-08-07).
 * 배열이 비어 있어도 "멤버 0명" 이라는 유효한 답이므로 true — 그 경우에도 팀 조회는 불필요하다.
 */
function hasMemberPreview(p: TaskProjectDto): boolean {
  return Array.isArray(p.memberPreview);
}

/** 완료 판정 — 아카이브됐거나, 태스크가 있고 전부 done. (서버에 상태 필드가 없어 집계로 대신) */
function isDone(p: TaskProjectDto): boolean {
  if (p.isArchived) return true;
  const total = p.taskCount ?? 0;
  return total > 0 && (p.doneTaskCount ?? 0) >= total;
}

/** YYYY-MM-DD / ISO → 'M.D' 표기(시안의 기간 표기와 같은 축약). */
function shortDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return `${d.getMonth() + 1}.${d.getDate()}`;
}

function ddayLabel(iso: string): string {
  const due = new Date(iso);
  if (Number.isNaN(due.getTime())) return '';
  const today = new Date();
  const a = Date.UTC(due.getFullYear(), due.getMonth(), due.getDate());
  const b = Date.UTC(today.getFullYear(), today.getMonth(), today.getDate());
  const days = Math.round((a - b) / 86_400_000);
  // ★ n 을 넘긴다 — 값이 'D+{n}' 이라 안 넘기면 화면에 「D+{n}」 글자가 그대로 뜬다.
  if (days < 0) return tTask('task.side.dday_over', { n: String(-days) });
  if (days === 0) return tTask('task.side.dday_today');
  return tTask('task.side.dday', { n: String(days) });
}
